import { Line, RangeModel } from './Enums';

export class Url {
  #hash;
  #params;
  #url;

  constructor() {
    this.#url = new URL(window.location.href);
    this.#params = new URLSearchParams(this.#url.search);
    this.#hash = this.#url.hash;
  }

  /**
   * Is this a screenshot request?
   * @returns {boolean}
   */
  isScreenshot() {
    this.#readUrl();

    return this.#params.get('screenshot') === 'true';
  }

  /**
   * Is this a summary page request?
   * @returns {boolean}
   */
  isSummary() {
    this.#readUrl();

    return this.#isOldSummary() || this.#isNewSummary();
  }

  /**
   * Is this a summary page request for a range?
   * @returns {boolean}
   */
  isRangeSummary() {
    return this.isSummary() && !!this.configuredRange();
  }

  /**
   * Is this running on the production (or staging) server?
   * @returns {boolean}
   */
  isProduction() {
    return (
      this.#url.hostname === 'configure.frenchranges.com' ||
      this.#url.hostname === 'staging.frenchranges.com'
    );
  }

  /**
   * Is this running on a development computer?
   * @returns {boolean}
   */
  isLocal() {
    return (
      this.#url.hostname === 'localhost' ||
      this.#url.hostname === 'configure.test'
    );
  }

  /**
   * Is a front view of the range requested?
   * @returns {boolean}
   */
  wantsFrontView() {
    this.#readUrl();

    return this.#params.get('view') === 'front';
  }

  /**
   * Is a top view of the range requested?
   * @returns {boolean}
   */
  wantsTopView() {
    this.#readUrl();

    return this.#params.get('view') === 'top';
  }

  /**
   * Is a top view of the range requested?
   * @returns {boolean}
   */
  wantsLabels() {
    this.#readUrl();

    return this.#params.get('labels') === 'true';
  }

  /**
   * Manually specified voltage in the URL.
   * @returns {?string}
   */
  voltageOverride() {
    this.#readUrl();

    return this.#params.get('volts');
  }

  /**
   * The requested range model in the Configurator URL.
   * Check both the query string and the hash.
   * @returns {?string}
   */
  requestedRange() {
    let model;

    this.#readUrl();

    if (!this.#hash.startsWith('#model/') && !this.#params.get('model')) {
      return null;
    }

    if (this.#params.get('model')) {
      model = this.#params.get('model');
    } else if (this.#hash.startsWith('#model/')) {
      model = this.#hash.substring(7);
      if (model.includes('-')) model = model.split('-')[0];
    }

    if (!Object.values(RangeModel).includes(model)) return null;

    return model;
  }

  /**
   * Extract the configured range from the configuration slug.
   * @returns {?object}
   */
  configuredRange() {
    if (!this.isSummary()) return null;

    let slug;

    if (this.#isOldSummary()) {
      slug = this.#hash.substring(9);
    } else {
      slug = this.#params.get('range');
    }

    if (!slug) return null;

    const parts = slug.split('-');
    if (parts.length < 9) return null;

    const [
      model,
      line,
      top,
      color,
      trim,
      leftOven,
      rightOven,
      gas,
      height,
      leftCupboard,
      rightCupboard,
      option1,
      option2,
      option3,
      option4,
    ] = parts;

    return {
      model: model || null,
      line: line || null,
      top: top || null,
      color: color || null,
      trim: trim || null,
      leftOven: leftOven || null,
      rightOven: rightOven || null,
      gas: gas || null,
      height: height || null,
      leftCupboard: leftCupboard || null,
      rightCupboard: rightCupboard || null,
      option1: option1 || null,
      option2: option2 || null,
      option3: option3 || null,
      option4: option4 || null,
    };
  }

  /**
   * Extract the configured hood from the configuration slug.
   * @returns {?object}
   */
  configuredHood() {
    this.#readUrl();
    if (!this.isSummary()) return null;

    const slug = this.#params.get('hood');

    if (!slug) return null;

    const parts = slug.split('-');
    if (parts.length < 6) return null;

    const [
      modelNumber,
      color,
      trim,
      ductCoverHeight,
      ductCoverFinish,
      insertChoice,
    ] = parts;

    return {
      modelNumber: modelNumber || null,
      color: color || null,
      trim: trim || null,
      dcHeight: ductCoverHeight || null,
      dcFinish: ductCoverFinish || null,
      insertChoice: insertChoice || null,
    };
  }

  /**
   * Extract the configured backsplash from the configuration slug.
   * @returns {?object}
   */
  configuredBacksplash() {
    this.#readUrl();
    if (!this.isSummary()) return null;

    const modelNumber = this.#params.get('backsplash');

    if (!modelNumber) return null;

    return { modelNumber };
  }

  /**
   * The requested range line in the Configurator URL.
   * Check both the query string and the hash.
   * @returns {?string}
   */
  requestedLine() {
    let line;

    this.#readUrl();

    if (!this.#hash.startsWith('#model/') && !this.#params.get('line')) {
      return null;
    }

    if (this.#params.get('line')) {
      line = this.#params.get('line');
    } else if (this.#hash.startsWith('#model/') && this.#hash.includes('-')) {
      line = this.#hash.split('-')[1];
    }

    if (line === 'moderne') line = 'mod';

    if (!Object.values(Line).includes(line)) return null;

    return line;
  }

  #isOldSummary() {
    return this.#hash.startsWith('#summary/');
  }

  #isNewSummary() {
    return (
      this.#url.pathname === '/summary' || this.#url.pathname === '/summary/'
    );
  }

  #readUrl() {
    this.#url = new URL(window.location.href);
    this.#params = new URLSearchParams(this.#url.search);
    this.#hash = this.#url.hash;
  }
}
